import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Check, AlertTriangle, Loader2 } from 'lucide-react';
import {
  defaultSerialise,
  fmtCurrencyDollars,
  fmtCentsAsDollars,
  editCentsAsDollars,
  boolDisplay,
  boolEditString,
  type FieldType,
} from './editableFieldHelpers';

// Re-export the helpers so consumers can `import {…} from 'EditableField'`
// rather than reaching for the helpers module directly.
export {
  defaultSerialise,
  fmtCurrencyDollars,
  fmtCentsAsDollars,
  editCentsAsDollars,
  boolDisplay,
  boolEditString,
  type FieldType,
};

const BOOL_OPTIONS: EditableFieldOption[] = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

/**
 * Spreadsheet-style cell for a single client field.
 *
 * UX:
 *  - Renders `displayFn(value)` (default: stringified value) until clicked.
 *  - On click → swaps to an input/select with the value pre-selected.
 *  - Debounced autosave 500ms after the last keystroke; also flushes on blur.
 *  - Esc reverts to the last server value. Enter commits + blurs.
 *  - Optimistic cache update via TanStack Query so the figure doesn't flash.
 *  - Inline status pip: 'saving' (spinner), 'saved' (check, 1.2s), 'error' (red).
 *
 * If `canEdit` is false the cell renders as plain text, no interaction.
 *
 * Currency variant: the field stores cents on the server but the user types
 * dollars. The component converts on submit and on display.
 */
export interface EditableFieldOption {
  value: string;
  label?: string;
}

export interface EditableFieldProps {
  clientId: string;
  fieldName: string; // server-side column name (must be in the allowlist)
  value: string | number | boolean | null | undefined;
  canEdit: boolean;
  type?: FieldType;
  options?: EditableFieldOption[]; // required when type='select'
  placeholder?: string;
  /** Display formatter — override for currency / dates / etc. */
  displayFn?: (v: string | number | boolean | null | undefined) => string;
  /** Override the payload's value — e.g. dollars → cents for currency. */
  serialiseFn?: (raw: string) => string | number | boolean | null;
  /** Override the input's initial textual value when editing. */
  editStringFn?: (v: string | number | boolean | null | undefined) => string;
  /** className for the static display state */
  className?: string;
  /** className for the input/select when editing */
  inputClassName?: string;
  /** Query key to invalidate after a successful save. Defaults to ['client', clientId, 'full']. */
  invalidateKey?: readonly unknown[];
  /** PATCH endpoint. Defaults to `/api/clients/:clientId`. Override for sub-resources
   *  (e.g. household members at `/api/clients/:clientId/members/:memberId`). */
  endpoint?: string;
  /** Optimistic cache patcher. Defaults to patching `data.client[fieldName]`.
   *  Override to patch a nested row (e.g. a member inside `data.members`). */
  cachePatch?: (cur: unknown, fieldName: string, value: unknown) => unknown;
}

const DEFAULT_DEBOUNCE_MS = 500;
const SAVED_PIP_MS = 1200;

type Status = 'idle' | 'saving' | 'saved' | 'error';

export function EditableField(props: EditableFieldProps) {
  const {
    clientId, fieldName, value, canEdit,
    type = 'text', options,
    placeholder, displayFn, serialiseFn, editStringFn,
    className, inputClassName,
    invalidateKey, endpoint, cachePatch,
  } = props;

  const qc = useQueryClient();
  const queryKey = useMemo(
    () => invalidateKey ?? (['client', clientId, 'full'] as const),
    [invalidateKey, clientId],
  );

  const display = (displayFn ?? (type === 'boolean' ? boolDisplay : defaultDisplay))(value);
  const initialString = (editStringFn ?? (type === 'boolean' ? boolEditString : defaultEditString))(value);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(initialString);
  const [status, setStatus] = useState<Status>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);

  // Re-sync the draft when the server value changes externally (e.g. after a
  // refetch from another tab) and we are NOT actively editing.
  useEffect(() => {
    if (!editing) setDraft(initialString);
  }, [initialString, editing]);

  // Focus the input when entering edit mode.
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      // For text-like inputs, select all so typing replaces.
      if (inputRef.current instanceof HTMLInputElement && type !== 'date') {
        inputRef.current.select();
      }
    }
  }, [editing, type]);

  // Cleanup pending timers on unmount.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
  }, []);

  const doSave = useCallback(async (raw: string) => {
    const payload = (serialiseFn ?? defaultSerialise(type))(raw);

    // Optimistic update: write straight into the cache so the view doesn't
    // wait for the round-trip. The server response will overwrite on success.
    const prev = qc.getQueryData<unknown>(queryKey);
    const patch = cachePatch ?? patchCachedClient;
    qc.setQueryData(queryKey, (cur: unknown) => patch(cur, fieldName, payload));

    setStatus('saving');
    try {
      await api(endpoint ?? `/api/clients/${clientId}`, {
        method: 'PATCH',
        body: JSON.stringify({ [fieldName]: payload }),
      });
      setStatus('saved');
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      savedTimerRef.current = setTimeout(() => setStatus('idle'), SAVED_PIP_MS);
      // Re-fetch to pick up any derived state (compliance, etc.).
      void qc.invalidateQueries({ queryKey });
    } catch (e) {
      // Revert on failure.
      qc.setQueryData(queryKey, prev);
      setStatus('error');
      // The inline error pip is the user signal. The ApiError detail is
      // available via the network response — no console noise.
      void e;
    }
  }, [clientId, fieldName, qc, queryKey, serialiseFn, type, endpoint, cachePatch]);

  const scheduleSave = useCallback((raw: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void doSave(raw); }, DEFAULT_DEBOUNCE_MS);
  }, [doSave]);

  const flushSave = useCallback((raw: string) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    // Only POST if the draft actually differs from the displayed value.
    if (raw !== initialString) {
      void doSave(raw);
    }
  }, [doSave, initialString]);

  function onChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const next = e.target.value;
    setDraft(next);
    // Select fires once; commit immediately rather than waiting for the debounce.
    if (type === 'select' || type === 'date' || type === 'boolean') {
      flushSave(next);
    } else {
      scheduleSave(next);
    }
  }

  function onBlur() {
    flushSave(draft);
    setEditing(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      setDraft(initialString);
      setEditing(false);
    } else if (e.key === 'Enter' && type !== 'select') {
      e.preventDefault();
      flushSave(draft);
      setEditing(false);
    }
  }

  if (!canEdit) {
    return <span className={className}>{display || <span className="text-ink-mute">—</span>}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => setEditing(true)}
        className={`group inline-flex items-center gap-1 text-left rounded px-1 -mx-1 hover:bg-amber-50/60 focus:outline-none focus:ring-1 focus:ring-rust transition-colors ${className ?? ''}`}
        title="Click to edit"
      >
        <span>{display || <span className="text-ink-mute italic">{placeholder ?? 'Click to add…'}</span>}</span>
        <StatusPip status={status} />
      </button>
    );
  }

  if (type === 'select' || type === 'boolean') {
    return (
      <span className="inline-flex items-center gap-1">
        <select
          ref={el => { inputRef.current = el; }}
          value={draft}
          onChange={onChange}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          className={`qa-input text-xs h-7 py-0 ${inputClassName ?? ''}`}
        >
          <option value="">—</option>
          {(type === 'boolean' ? BOOL_OPTIONS : (options ?? [])).map(o => (
            <option key={o.value} value={o.value}>{o.label ?? o.value}</option>
          ))}
        </select>
        <StatusPip status={status} />
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={el => { inputRef.current = el; }}
        type={inputType(type)}
        value={draft}
        onChange={onChange}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={`qa-input text-xs h-7 py-0 ${inputClassName ?? ''}`}
      />
      <StatusPip status={status} />
    </span>
  );
}

function StatusPip({ status }: { status: Status }) {
  if (status === 'saving') return <Loader2 size={10} className="animate-spin text-ink-mute" aria-label="Saving" />;
  if (status === 'saved')  return <Check size={10} className="text-emerald-600" aria-label="Saved" />;
  if (status === 'error')  return <AlertTriangle size={10} className="text-rose-600" aria-label="Save failed" />;
  return null;
}

function inputType(t: FieldType): string {
  switch (t) {
    case 'email':    return 'email';
    case 'tel':      return 'tel';
    case 'date':     return 'date';
    case 'number':
    case 'currency': return 'number';
    default:         return 'text';
  }
}

function defaultDisplay(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined || v === '') return '';
  return String(v);
}

function defaultEditString(v: string | number | boolean | null | undefined): string {
  if (v === null || v === undefined) return '';
  return String(v);
}


/**
 * Patch a single field of the `client` blob inside the cached
 * `/api/clients/:id/full` payload. Tolerant of cache miss.
 */
function patchCachedClient(cur: unknown, fieldName: string, value: unknown): unknown {
  if (!cur || typeof cur !== 'object') return cur;
  const obj = cur as { data?: { client?: Record<string, unknown> } };
  if (!obj.data?.client) return cur;
  return {
    ...obj,
    data: {
      ...obj.data,
      client: { ...obj.data.client, [fieldName]: value },
    },
  };
}

